import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import type { TextLine, RealmId, SceneChoice } from "@chuzi/shared/types";
import {
  SCENE_TEXT_ARROW_SAFE_INSET,
  sceneTextFontCss,
  sceneTextFontOptions,
} from "@chuzi/shared/config";
import { t } from "@chuzi/shared/config";

/* ─── Constants ─── */

const TEXT_FONT_OPTIONS = sceneTextFontOptions();

/** Default dramatic text scale (percentage). */
export const DEFAULT_SCENE_FONT_SIZE = 100;

export function normalizeSceneFontSize(value?: number | null): number {
  if (value == null || !Number.isFinite(value)) return DEFAULT_SCENE_FONT_SIZE;
  return Math.max(50, Math.min(150, Math.round(value)));
}

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const frac = Math.floor((ms % 1000) / 100);
  return m > 0 ? `${m}:${sec.toString().padStart(2, "0")}.${frac}` : `${sec}.${frac}s`;
}

/* ─── RichTextLineEditor ─── */

interface RichTextLineEditorProps {
  html: string;
  realm: RealmId;
  onChange: (html: string) => void;
  onEditorReady?: (editor: any) => void;
}

export function RichTextLineEditor({ html, realm, onChange, onEditorReady }: RichTextLineEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      TextAlign.configure({
        types: ["paragraph", "bulletList", "orderedList"],
      }),
      Placeholder.configure({
        placeholder: t(realm, "editor_text_placeholder", "Start writing..."),
      }),
      TextStyle,
      Color,
    ],
    content: html,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor && editor.getHTML() !== html) {
      editor.commands.setContent(html, { emitUpdate: false });
    }
  }, [html, editor]);

  return (
    <div
      style={{
        border: "none",
        borderRadius: 0,
        background: "transparent",
        minHeight: 48,
        padding: 10,
      }}
      className="tiptap-editor-wrapper"
    >
      <EditorContent editor={editor} />
      <style>{`
        .tiptap-editor-wrapper .tiptap {
          outline: none;
          min-height: 32px;
          color: #e8f0ff;
          font-size: 14px;
          line-height: 1.6;
        }
        .tiptap-editor-wrapper .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: rgba(232,240,255,0.3);
          pointer-events: none;
          float: left;
          height: 0;
        }
        .tiptap-editor-wrapper .tiptap ul,
        .tiptap-editor-wrapper .tiptap ol {
          padding-left: 1.2em;
          color: #e8f0ff;
        }
      `}</style>
    </div>
  );
}

/* ─── FormattingToolbar ─── */

interface FormattingToolbarProps {
  realm: RealmId;
  editorHtml: string;
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
  fontSize?: number;
  onStyleChange: (next: {
    fontFamily?: string;
    textColor?: string;
    backgroundColor?: string;
    fontSize?: number;
  }) => void;
  onBold: () => void;
  onItalic: () => void;
  onStrike: () => void;
  onBulletList: () => void;
  onOrderedList: () => void;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignRight: () => void;
  onEmojiInsert: (emoji: string) => void;
  activeStates: {
    bold: boolean;
    italic: boolean;
    strike: boolean;
    bulletList: boolean;
    orderedList: boolean;
    alignLeft: boolean;
    alignCenter: boolean;
    alignRight: boolean;
  };
}

export function FormattingToolbar({
  realm,
  fontFamily,
  textColor,
  backgroundColor,
  fontSize = DEFAULT_SCENE_FONT_SIZE,
  onStyleChange,
  onBold,
  onItalic,
  onStrike,
  onBulletList,
  onOrderedList,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onEmojiInsert,
  activeStates,
}: FormattingToolbarProps) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
      <ToolbarBtn active={activeStates.bold} onClick={onBold} title="Bold">B</ToolbarBtn>
      <ToolbarBtn active={activeStates.italic} onClick={onItalic} title="Italic"><em>I</em></ToolbarBtn>
      <ToolbarBtn active={activeStates.strike} onClick={onStrike} title="Strikethrough"><s>S</s></ToolbarBtn>
      <ToolbarSep />
      <ToolbarBtn active={activeStates.bulletList} onClick={onBulletList} title="Bullet List">
        <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="3" cy="4" r="1.5" fill="currentColor"/><circle cx="3" cy="7" r="1.5" fill="currentColor"/><circle cx="3" cy="10" r="1.5" fill="currentColor"/><rect x="6" y="3.5" width="6" height="1" rx="0.5" fill="currentColor"/><rect x="6" y="6.5" width="6" height="1" rx="0.5" fill="currentColor"/><rect x="6" y="9.5" width="6" height="1" rx="0.5" fill="currentColor"/></svg>
      </ToolbarBtn>
      <ToolbarBtn active={activeStates.orderedList} onClick={onOrderedList} title="Numbered List">
        <svg width="14" height="14" viewBox="0 0 14 14"><text x="1" y="5" fontSize="4" fill="currentColor">1.</text><text x="1" y="8" fontSize="4" fill="currentColor">2.</text><text x="1" y="11" fontSize="4" fill="currentColor">3.</text><rect x="6" y="3.5" width="6" height="1" rx="0.5" fill="currentColor"/><rect x="6" y="6.5" width="6" height="1" rx="0.5" fill="currentColor"/><rect x="6" y="9.5" width="6" height="1" rx="0.5" fill="currentColor"/></svg>
      </ToolbarBtn>
      <ToolbarSep />
      <ToolbarBtn active={activeStates.alignLeft} onClick={onAlignLeft} title="Align Left">
        <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="3" width="12" height="1" rx="0.5" fill="currentColor"/><rect x="1" y="6" width="8" height="1" rx="0.5" fill="currentColor"/><rect x="1" y="9" width="10" height="1" rx="0.5" fill="currentColor"/></svg>
      </ToolbarBtn>
      <ToolbarBtn active={activeStates.alignCenter} onClick={onAlignCenter} title="Align Center">
        <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="3" width="12" height="1" rx="0.5" fill="currentColor"/><rect x="3" y="6" width="8" height="1" rx="0.5" fill="currentColor"/><rect x="2" y="9" width="10" height="1" rx="0.5" fill="currentColor"/></svg>
      </ToolbarBtn>
      <ToolbarBtn active={activeStates.alignRight} onClick={onAlignRight} title="Align Right">
        <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="3" width="12" height="1" rx="0.5" fill="currentColor"/><rect x="5" y="6" width="8" height="1" rx="0.5" fill="currentColor"/><rect x="3" y="9" width="10" height="1" rx="0.5" fill="currentColor"/></svg>
      </ToolbarBtn>
      <ToolbarSep />
      <div style={{ position: "relative" }}>
        <ToolbarBtn active={emojiOpen} onClick={() => setEmojiOpen(!emojiOpen)} title="Emoji">
          <span style={{ fontSize: 14 }}>😀</span>
        </ToolbarBtn>
        {emojiOpen && (
          <EmojiPicker
            onSelect={(emoji) => { onEmojiInsert(emoji); setEmojiOpen(false); }}
            onClose={() => setEmojiOpen(false)}
          />
        )}
      </div>
      <ToolbarSep />
      <select
        value={fontFamily}
        onChange={(e) => onStyleChange({ fontFamily: e.target.value })}
        style={{
          width: 148,
          borderRadius: 6,
          border: "1px solid rgba(232,240,255,0.2)",
          background: "rgba(232,240,255,0.06)",
          color: "#e8f0ff",
          padding: "3px 4px",
          fontSize: 10,
          fontFamily: sceneTextFontCss(fontFamily),
        }}
      >
        {TEXT_FONT_OPTIONS.map((font) => (
          <option key={font.value} value={font.value} style={{ fontFamily: font.family }}>
            {font.label}
          </option>
        ))}
      </select>
      <ToolbarSep />
      <div
        style={{ display: "flex", alignItems: "center", gap: 4 }}
        title={t(realm, "editor_text_font_size_label", "Font Size")}
      >
        <button
          type="button"
          onClick={() => onStyleChange({ fontSize: Math.max(50, fontSize - 5) })}
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            border: "1px solid rgba(232,240,255,0.2)",
            background: "rgba(232,240,255,0.06)",
            color: "#e8f0ff",
            cursor: "pointer",
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          −
        </button>
        <span style={{
          fontSize: 10,
          color: "rgba(232,240,255,0.7)",
          minWidth: 36,
          textAlign: "center",
          fontVariantNumeric: "tabular-nums",
        }}>
          {fontSize}%
        </span>
        <button
          type="button"
          onClick={() => onStyleChange({ fontSize: Math.min(150, fontSize + 5) })}
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            border: "1px solid rgba(232,240,255,0.2)",
            background: "rgba(232,240,255,0.06)",
            color: "#e8f0ff",
            cursor: "pointer",
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          +
        </button>
      </div>
      <ToolbarSep />
      <input
        type="color"
        value={textColor}
        onChange={(e) => onStyleChange({ textColor: e.target.value })}
        title={t(realm, "editor_text_color_label", "Text Color")}
        style={{ width: 22, height: 22, border: "none", cursor: "pointer", padding: 0 }}
      />
      <input
        type="color"
        value={backgroundColor}
        onChange={(e) => onStyleChange({ backgroundColor: e.target.value })}
        title={t(realm, "editor_text_background_label", "Background")}
        style={{ width: 22, height: 22, border: "none", cursor: "pointer", padding: 0 }}
      />
    </div>
  );
}

function ToolbarBtn({ active, onClick, title, children }: { active: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 28,
        height: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 4,
        border: "none",
        background: active ? "rgba(126,184,255,0.25)" : "rgba(232,240,255,0.06)",
        color: active ? "#7eb8ff" : "rgba(232,240,255,0.7)",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 700,
        transition: "background 0.15s",
      }}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <div style={{ width: 1, height: 20, background: "rgba(232,240,255,0.12)", margin: "0 2px" }} />;
}

/* ─── Emoji Picker (lightweight inline grid) ─── */

const COMMON_EMOJIS = [
  "😀","😂","🥹","😍","🤔","😎","🥺","😡","💀","👻",
  "❤️","🔥","⭐","✨","💫","🌙","☀️","🌊","🌸","🍃",
  "👍","👎","👏","🙏","💪","🎉","🎵","📖","✍️","💡",
  "⚡","🚀","🌍","🎬","🎭","🎨","📸","🎤","💬","🔮",
];

function EmojiPicker({ onSelect, onClose }: { onSelect: (emoji: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        bottom: 34,
        left: 0,
        zIndex: 200,
        background: "rgba(10,16,32,0.95)",
        border: "1px solid rgba(126,184,255,0.3)",
        borderRadius: 10,
        padding: 8,
        width: 220,
        display: "grid",
        gridTemplateColumns: "repeat(8, 1fr)",
        gap: 2,
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      }}
    >
      {COMMON_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onSelect(emoji)}
          style={{
            background: "none",
            border: "none",
            fontSize: 16,
            cursor: "pointer",
            padding: 3,
            borderRadius: 4,
            transition: "background 0.1s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(126,184,255,0.15)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

/* ─── TextLineList (drag/drop reorderable) ─── */

interface TextLineListProps {
  lines: TextLine[];
  selectedId: string | null;
  realm: RealmId;
  onSelect: (id: string) => void;
  onReorder: (lines: TextLine[]) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  disabledLines?: TextLine[];
  showAddButton?: boolean;
}

export function TextLineList({ lines, selectedId, realm, onSelect, onReorder, onAdd, onDelete, disabledLines, showAddButton = true }: TextLineListProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const accentDim = isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)";

  useEffect(() => {
    return () => { if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current); };
  }, []);

  const handleDeleteClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirmingDeleteId === id) {
      if (deleteTimerRef.current) { clearTimeout(deleteTimerRef.current); deleteTimerRef.current = null; }
      setConfirmingDeleteId(null);
      onDelete(id);
    } else {
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      setConfirmingDeleteId(id);
      deleteTimerRef.current = setTimeout(() => {
        setConfirmingDeleteId(null);
        deleteTimerRef.current = null;
      }, 3000);
    }
  }, [confirmingDeleteId, onDelete]);

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverIdx(idx);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const reordered = [...lines];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    onReorder(reordered);
    setDragIdx(null);
    setOverIdx(null);
  }, [dragIdx, lines, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setOverIdx(null);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {lines.map((line, idx) => {
        const isSelected = line.id === selectedId;
        const isDragOver = overIdx === idx && dragIdx !== idx;
        const textPreview = line.html.replace(/<[^>]*>/g, "").slice(0, 50) || "(empty)";
        return (
          <div
            key={line.id}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            onClick={() => onSelect(line.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 6,
              border: isSelected
                ? `1px solid ${accent}`
                : isDragOver
                  ? "1px solid rgba(232,240,255,0.4)"
                  : "1px solid rgba(232,240,255,0.1)",
              background: isSelected ? accentDim : "rgba(232,240,255,0.03)",
              cursor: "grab",
              opacity: dragIdx === idx ? 0.5 : 1,
              transition: "border-color 0.15s, background 0.15s",
              userSelect: "none",
            }}
          >
            <div style={{ color: "rgba(232,240,255,0.3)", fontSize: 10, cursor: "grab" }}>⠿</div>
            <div style={{ flex: 1, fontSize: 12, color: "#e8f0ff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {textPreview}
            </div>
            <div style={{ fontSize: 9, color: "rgba(232,240,255,0.45)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
              {formatMs(line.appear_at_ms)}
            </div>
            <button
              type="button"
              onClick={(e) => handleDeleteClick(e, line.id)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                border: confirmingDeleteId === line.id
                  ? "1px solid rgba(255,107,107,0.7)"
                  : "1px solid transparent",
                background: confirmingDeleteId === line.id
                  ? "rgba(255,107,107,0.2)"
                  : "rgba(255,107,107,0.1)",
                color: confirmingDeleteId === line.id
                  ? "#ff8d8d"
                  : "rgba(255,150,150,0.7)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s",
              }}
              title={confirmingDeleteId === line.id ? "Click again to confirm" : "Remove line"}
            >
              {confirmingDeleteId === line.id ? "?" : "×"}
            </button>
          </div>
        );
      })}
      {disabledLines && disabledLines.length > 0 && disabledLines.map((line, idx) => (
        <div
          key={line.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 8px",
            borderRadius: 5,
            background: "rgba(232,240,255,0.03)",
            border: "1px solid rgba(232,240,255,0.08)",
            opacity: 0.5,
            fontSize: 11,
            color: "rgba(232,240,255,0.55)",
            cursor: "default",
          }}
        >
          <span style={{ fontSize: 8, color: "rgba(232,240,255,0.3)" }}>
            {line.type === "sound" ? "♪" : "◻"}{idx + 1}
          </span>
          <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {line.html.replace(/<[^>]*>/g, "") || `(${line.type})`}
          </div>
          <div style={{ fontSize: 9, color: "rgba(232,240,255,0.35)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {formatMs(line.appear_at_ms)}
          </div>
        </div>
      ))}
      {showAddButton && (
        <button
          type="button"
          onClick={onAdd}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            border: `1px dashed ${isWilds ? "rgba(120,200,120,0.3)" : "rgba(126,184,255,0.3)"}`,
            background: "transparent",
            color: accent,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.04em",
          }}
        >
          + Add Text
        </button>
      )}
    </div>
  );
}

/* ─── TimelineStrip ─── */

interface TimelineStripProps {
  lines: TextLine[];
  selectedId: string | null;
  realm: RealmId;
  playheadMs: number;
  totalDurationMs: number;
  onSelect: (id: string) => void;
  onLineUpdate: (id: string, update: Partial<TextLine>) => void;
}

export function TimelineStrip({
  lines,
  selectedId,
  realm,
  playheadMs,
  totalDurationMs,
  onSelect,
  onLineUpdate,
}: TimelineStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{ lineId: string; type: "move" | "fade_in" | "fade_out" | "duration"; startX: number; startVal: number } | null>(null);
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";
  const accentBright = isWilds ? "rgba(120,200,120,1)" : "rgba(126,184,255,1)";

  const duration = Math.max(totalDurationMs, 5000);
  const pxPerMs = containerRef.current ? (containerRef.current.clientWidth - 40) / duration : 0.1;

  const tickInterval = duration <= 5000 ? 1000 : duration <= 15000 ? 2000 : 5000;
  const ticks: number[] = [];
  for (let t = 0; t <= duration; t += tickInterval) ticks.push(t);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      const dx = e.clientX - dragging.startX;
      const dMs = Math.round(dx / pxPerMs);
      const line = lines.find((l) => l.id === dragging.lineId);
      if (!line) return;

      if (dragging.type === "move") {
        onLineUpdate(dragging.lineId, { appear_at_ms: Math.max(0, dragging.startVal + dMs) });
      } else if (dragging.type === "fade_in") {
        onLineUpdate(dragging.lineId, { fade_in_ms: Math.max(0, dragging.startVal + dMs) });
      } else if (dragging.type === "fade_out") {
        onLineUpdate(dragging.lineId, { fade_out_ms: Math.max(0, dragging.startVal + dMs) });
      } else if (dragging.type === "duration") {
        onLineUpdate(dragging.lineId, { duration_ms: Math.max(100, dragging.startVal + dMs) });
      }
    };
    const handleUp = () => setDragging(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging, pxPerMs, lines, onLineUpdate]);

  const playheadLeft = 20 + playheadMs * pxPerMs;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        background: "rgba(4,7,13,0.9)",
        border: "1px solid rgba(232,240,255,0.12)",
        borderRadius: 8,
        padding: "4px 0",
        minHeight: 40 + lines.length * 28,
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      {/* Time ruler */}
      <div style={{ height: 18, position: "relative", marginLeft: 20, marginRight: 20 }}>
        {ticks.map((ms) => (
          <div
            key={ms}
            style={{
              position: "absolute",
              left: `${(ms / duration) * 100}%`,
              top: 0,
              fontSize: 8,
              color: "rgba(232,240,255,0.35)",
              transform: "translateX(-50%)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)}s
          </div>
        ))}
      </div>

      {/* Tracks */}
      {lines.map((line, idx) => {
        const isSelected = line.id === selectedId;
        const left = 20 + (line.appear_at_ms / duration) * (containerRef.current ? containerRef.current.clientWidth - 40 : 300);
        const totalWidth = ((line.fade_in_ms + line.duration_ms + line.fade_out_ms) / duration) * (containerRef.current ? containerRef.current.clientWidth - 40 : 300);
        const fadeInWidth = (line.fade_in_ms / duration) * (containerRef.current ? containerRef.current.clientWidth - 40 : 300);
        const fadeOutWidth = (line.fade_out_ms / duration) * (containerRef.current ? containerRef.current.clientWidth - 40 : 300);

        return (
          <div
            key={line.id}
            style={{ position: "relative", height: 24, marginBottom: 4 }}
            onClick={() => onSelect(line.id)}
          >
            {/* Main bar */}
            <div
              style={{
                position: "absolute",
                left,
                top: 4,
                width: Math.max(totalWidth, 8),
                height: 16,
                borderRadius: 4,
                background: isSelected ? accent : "rgba(126,184,255,0.35)",
                border: isSelected ? `1px solid ${accentBright}` : "1px solid rgba(126,184,255,0.2)",
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setDragging({ lineId: line.id, type: "move", startX: e.clientX, startVal: line.appear_at_ms });
              }}
            >
              {/* Fade-in region */}
              {fadeInWidth > 2 && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: fadeInWidth,
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.15))",
                    borderRadius: "4px 0 0 4px",
                    cursor: "ew-resize",
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setDragging({ lineId: line.id, type: "fade_in", startX: e.clientX, startVal: line.fade_in_ms });
                  }}
                />
              )}
              {/* Fade-out region */}
              {fadeOutWidth > 2 && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: fadeOutWidth,
                    height: "100%",
                    background: "linear-gradient(270deg, transparent, rgba(255,255,255,0.15))",
                    borderRadius: "0 4px 4px 0",
                    cursor: "ew-resize",
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setDragging({ lineId: line.id, type: "fade_out", startX: e.clientX, startVal: line.fade_out_ms });
                  }}
                />
              )}
              {/* Duration handle (right edge) */}
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: 6,
                  height: "100%",
                  cursor: "ew-resize",
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging({ lineId: line.id, type: "duration", startX: e.clientX, startVal: line.duration_ms });
                }}
              />
            </div>
          </div>
        );
      })}

      {/* Playhead */}
      <div
        style={{
          position: "absolute",
          left: playheadLeft,
          top: 0,
          bottom: 0,
          width: 1,
          background: "#ff6b6b",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: playheadLeft - 4,
          top: 0,
          width: 0,
          height: 0,
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderTop: "6px solid #ff6b6b",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
    </div>
  );
}

/* ─── TextPreviewPanel ─── */

export const SCROLLABLE_TEXT_FILL = {
  position_x: 0,
  position_y: 0,
  width_pct: 100,
  height_pct: 100,
} as const;

export function isScrollableTextLine(line: TextLine): boolean {
  return (!line.type || line.type === "text") && line.height_pct != null && line.height_pct > 0;
}

export function isFullScreenTextLine(line: TextLine, textOnlyLineCount: number): boolean {
  if (line.type && line.type !== "text") return false;
  return isScrollableTextLine(line) || (
    textOnlyLineCount === 1 && line.html.replace(/<[^>]*>/g, "").trim().length > 0
  );
}

export const DRAMATIC_TEXT_PREVIEW_CLASS = "chuzi-dramatic-text";

/** Tight leading for full-width scene prose (never columnar). */
export const DRAMATIC_TEXT_LINE_HEIGHT = 1.12;

export function isFillLayoutLine(line: TextLine): boolean {
  if (line.type && line.type !== "text") return false;
  const width = line.width_pct ?? 100;
  const height = line.height_pct ?? 0;
  return width >= 99 && height >= 99;
}

export function dramaticTextContentStyle(options?: {
  compact?: boolean;
  containerRelative?: boolean;
  fontSizeScale?: number;
}) {
  const scale = normalizeSceneFontSize(options?.fontSizeScale) / 100;
  const unit = options?.containerRelative ? "cqmin" : "vmin";
  const minRem = options?.compact ? 0.55 : 1.15;
  const mid = options?.compact ? 2.8 : 4.2;
  const maxRem = options?.compact ? 1.35 : 3.25;
  const baseFont = `clamp(${minRem}rem, ${mid}${unit}, ${maxRem}rem)`;
  return {
    fontSize: scale === 1 ? baseFont : `calc(${baseFont} * ${scale})`,
    lineHeight: DRAMATIC_TEXT_LINE_HEIGHT,
    letterSpacing: "0.02em",
    fontWeight: 500,
    textShadow: options?.compact
      ? "0 2px 16px rgba(0,0,0,0.85)"
      : "0 3px 28px rgba(0,0,0,0.45)",
    wordBreak: "break-word" as const,
  };
}

export function fullScreenTextContainerStyle(options?: {
  compact?: boolean;
  containerRelative?: boolean;
  fontSizeScale?: number;
}) {
  return {
    inset: 0,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "stretch",
    padding: `${SCENE_TEXT_ARROW_SAFE_INSET.top} ${SCENE_TEXT_ARROW_SAFE_INSET.right} ${SCENE_TEXT_ARROW_SAFE_INSET.bottom} ${SCENE_TEXT_ARROW_SAFE_INSET.left}`,
    boxSizing: "border-box" as const,
    ...dramaticTextContentStyle(options),
  };
}

/** Positioned text box for the interactive editor preview (container-relative sizing). */
export function editorTextBoxStyle(
  line: TextLine,
  idx: number,
  scrollable: boolean,
  fontSizeScale?: number,
) {
  const scale = normalizeSceneFontSize(fontSizeScale) / 100;
  const baseFont = "clamp(11px, 2.4cqmin, 18px)";
  return {
    left: `${line.position_x ?? 5}%`,
    top: `${line.position_y ?? idx * 20 + 10}%`,
    width: scrollable && line.width_pct != null ? `${line.width_pct}%` : "auto",
    maxWidth: "90%",
    height: scrollable && line.height_pct != null ? `${line.height_pct}%` : "auto",
    minHeight: scrollable ? 48 : undefined,
    fontSize: scale === 1 ? baseFont : `calc(${baseFont} * ${scale})`,
    lineHeight: 1.45,
    padding: scrollable ? "10px 14px" : "2px 6px",
    boxSizing: "border-box" as const,
    overflowY: scrollable ? ("auto" as const) : ("hidden" as const),
    overflowX: "hidden" as const,
  };
}

export function fillLayoutBoxStyle(
  line: TextLine,
  options?: { compact?: boolean; fontSizeScale?: number },
) {
  return {
    left: `${line.position_x ?? 0}%`,
    top: `${line.position_y ?? 0}%`,
    width: `${line.width_pct ?? 100}%`,
    height: `${line.height_pct ?? 100}%`,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "stretch",
    padding: `${SCENE_TEXT_ARROW_SAFE_INSET.top} ${SCENE_TEXT_ARROW_SAFE_INSET.right} ${SCENE_TEXT_ARROW_SAFE_INSET.bottom} ${SCENE_TEXT_ARROW_SAFE_INSET.left}`,
    boxSizing: "border-box" as const,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    ...dramaticTextContentStyle({
      containerRelative: true,
      fontSizeScale: options?.fontSizeScale,
      compact: options?.compact,
    }),
  };
}

function usePreviewBoxResize(
  lineId: string,
  containerRef: React.RefObject<HTMLDivElement | null>,
  readOnly: boolean,
  onResize?: (id: string, w: number, h: number) => void,
) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const lastSizeRef = useRef<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (readOnly || !onResize || !elRef.current || !containerRef.current) return;
    const el = elRef.current;
    const container = containerRef.current;
    const ro = new ResizeObserver(() => {
      const cr = container.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      if (cr.width <= 0 || cr.height <= 0) return;
      const w = Math.round((er.width / cr.width) * 1000) / 10;
      const h = Math.round((er.height / cr.height) * 1000) / 10;
      const last = lastSizeRef.current;
      if (last && Math.abs(last.w - w) < 0.5 && Math.abs(last.h - h) < 0.5) return;
      lastSizeRef.current = { w, h };
      onResize(
        lineId,
        Math.max(5, Math.min(100, w)),
        Math.max(5, Math.min(100, h)),
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [lineId, readOnly, onResize, containerRef]);

  return elRef;
}

interface PreviewTextLineProps {
  line: TextLine & { opacity: number };
  idx: number;
  textOnlyLineCount: number;
  readOnly: boolean;
  previewRef: React.RefObject<HTMLDivElement | null>;
  draggingId: string | null;
  accent: string;
  resolvedFontFamily: string;
  textColor: string;
  backgroundColor: string;
  fontSize?: number;
  onDragStart: (e: React.MouseEvent, lineId: string) => void;
  onResize?: (id: string, w: number, h: number) => void;
}

function PreviewTextLine({
  line,
  idx,
  textOnlyLineCount,
  readOnly,
  previewRef,
  draggingId,
  accent,
  resolvedFontFamily,
  textColor,
  backgroundColor,
  fontSize = DEFAULT_SCENE_FONT_SIZE,
  onDragStart,
  onResize,
}: PreviewTextLineProps) {
  const dramaticFill = readOnly && isFullScreenTextLine(line, textOnlyLineCount);
  const fillLayout = isFillLayoutLine(line);
  const scrollable = isScrollableTextLine(line) || dramaticFill || fillLayout;
  const editable = !readOnly;
  const canResize = editable && scrollable && !!onResize;
  const resizeRef = usePreviewBoxResize(line.id, previewRef, readOnly, canResize ? onResize : undefined);
  const isDragging = draggingId === line.id;
  const useFillBox = fillLayout && (readOnly ? dramaticFill : true);
  const layoutStyle = useFillBox
    ? (readOnly
      ? fullScreenTextContainerStyle({ compact: true, fontSizeScale: fontSize })
      : fillLayoutBoxStyle(line, { fontSizeScale: fontSize }))
    : editorTextBoxStyle(line, idx, scrollable, fontSize);

  return (
    <div
      ref={resizeRef}
      className={(useFillBox || dramaticFill) ? DRAMATIC_TEXT_PREVIEW_CLASS : undefined}
      onMouseDown={editable ? (e) => onDragStart(e, line.id) : undefined}
      style={{
        position: "absolute",
        ...layoutStyle,
        opacity: line.opacity,
        transition: isDragging ? "none" : "opacity 0.05s linear",
        fontFamily: resolvedFontFamily,
        color: textColor,
        cursor: editable ? "grab" : "default",
        userSelect: "none",
        borderRadius: useFillBox && !editable ? 0 : useFillBox ? 0 : 4,
        border: editable
          ? `1px dashed ${isDragging ? accent : `${accent}66`}`
          : "1px dashed transparent",
        background: editable && scrollable ? backgroundColor : isDragging ? "rgba(126,184,255,0.06)" : "transparent",
        resize: canResize ? "both" : "none",
        overflow: canResize ? "auto" : undefined,
        wordBreak: "break-word",
      }}
      dangerouslySetInnerHTML={{ __html: line.html }}
    />
  );
}

interface PreviewEffectLineProps {
  line: TextLine & { opacity: number };
  readOnly: boolean;
  previewRef: React.RefObject<HTMLDivElement | null>;
  draggingId: string | null;
  isWilds: boolean;
  selectedEffectId?: string | null;
  mediaUrls?: Record<string, string>;
  onDragStart: (e: React.MouseEvent, lineId: string) => void;
  onResize?: (id: string, w: number, h: number) => void;
}

function PreviewEffectLine({
  line,
  readOnly,
  previewRef,
  draggingId,
  isWilds,
  selectedEffectId,
  mediaUrls,
  onDragStart,
  onResize,
}: PreviewEffectLineProps) {
  const isSound = line.type === "sound";
  const effectTokenColor = isWilds ? "#b78aff" : "#ffb347";
  const isSelected = line.id === selectedEffectId;
  const canResize = !readOnly && !isSound && !!onResize;
  const resizeRef = usePreviewBoxResize(line.id, previewRef, readOnly, canResize ? onResize : undefined);
  const isDragging = draggingId === line.id;

  return (
    <div
      ref={resizeRef}
      onMouseDown={(e) => onDragStart(e, line.id)}
      style={{
        position: "absolute",
        left: `${line.position_x ?? 35}%`,
        top: `${line.position_y ?? 35}%`,
        width: isSound ? 60 : `${line.width_pct ?? 30}%`,
        height: isSound ? 60 : `${line.height_pct ?? 30}%`,
        opacity: line.opacity,
        border: readOnly ? "none" : `2px dashed ${isSelected ? effectTokenColor : `${effectTokenColor}88`}`,
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: readOnly ? "default" : "grab",
        userSelect: "none",
        background: readOnly ? "transparent" : `${effectTokenColor}0a`,
        transition: isDragging ? "none" : "opacity 0.05s linear",
        resize: canResize ? "both" : "none",
        overflow: isSound ? "visible" : "hidden",
      }}
    >
      {!isSound && line.media_id && mediaUrls?.[line.media_id] ? (
        <img
          src={mediaUrls[line.media_id]}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : (
        <span style={{ fontSize: isSound ? 20 : 14, color: effectTokenColor, opacity: readOnly ? 0 : 0.8 }}>
          {isSound ? "♪" : "◻"}
        </span>
      )}
    </div>
  );
}

export function dramaticTextStylesheet(): string {
  return `
    .${DRAMATIC_TEXT_PREVIEW_CLASS} p {
      margin: 0 0 0.32em;
      width: 100%;
      text-align: left;
      column-count: 1 !important;
      columns: auto !important;
    }
    .${DRAMATIC_TEXT_PREVIEW_CLASS} p:last-child {
      margin-bottom: 0;
    }
    .${DRAMATIC_TEXT_PREVIEW_CLASS} strong {
      font-weight: 700;
    }
    .${DRAMATIC_TEXT_PREVIEW_CLASS} em {
      font-style: italic;
    }
  `;
}

/** Percent position for editor preview drag — no viewport clamping. */
function roundPreviewPositionPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

interface TextPreviewPanelProps {
  lines: TextLine[];
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
  fontSize?: number;
  realm: RealmId;
  playheadMs: number;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (ms: number) => void;
  totalDurationMs: number;
  onLinePositionChange?: (id: string, x: number, y: number) => void;
  selectedEffectId?: string | null;
  onEffectResize?: (id: string, w: number, h: number) => void;
  overlay?: ReactNode;
  onPreviewAreaRef?: (el: HTMLDivElement | null) => void;
  readOnly?: boolean;
  showTransport?: boolean;
  mediaUrls?: Record<string, string>;
  headerLabel?: string;
  fullScreen?: boolean;
}

export function TextPreviewPanel({
  lines,
  fontFamily,
  textColor,
  backgroundColor,
  fontSize = DEFAULT_SCENE_FONT_SIZE,
  realm,
  playheadMs,
  isPlaying,
  onPlay,
  onPause,
  onSeek,
  totalDurationMs,
  onLinePositionChange,
  selectedEffectId,
  onEffectResize,
  overlay,
  onPreviewAreaRef,
  readOnly = false,
  showTransport = true,
  mediaUrls,
  headerLabel,
  fullScreen = false,
}: TextPreviewPanelProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ ox: number; oy: number }>({ ox: 0, oy: 0 });

  const visibleLines = useMemo(() => {
    return lines.map((line) => {
      const start = line.appear_at_ms;
      const fadeInEnd = start + line.fade_in_ms;
      const visibleEnd = fadeInEnd + line.duration_ms;
      const fadeOutEnd = visibleEnd + line.fade_out_ms;

      const persist = line.persist !== false;

      let opacity = 0;
      if (playheadMs < start) {
        opacity = 0;
      } else if (playheadMs < fadeInEnd) {
        opacity = line.fade_in_ms > 0 ? (playheadMs - start) / line.fade_in_ms : 1;
      } else if (playheadMs < visibleEnd) {
        opacity = 1;
      } else if (playheadMs < fadeOutEnd) {
        opacity = persist ? 1 : (line.fade_out_ms > 0 ? 1 - (playheadMs - visibleEnd) / line.fade_out_ms : 0);
      } else {
        opacity = persist ? 1 : 0;
      }

      return { ...line, opacity };
    });
  }, [lines, playheadMs]);

  const handleDragStart = useCallback((e: React.MouseEvent, lineId: string) => {
    if (readOnly) return;
    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const HANDLE_ZONE = 18;
    const nearRight = e.clientX >= rect.right - HANDLE_ZONE;
    const nearBottom = e.clientY >= rect.bottom - HANDLE_ZONE;
    if (nearRight && nearBottom) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggingId(lineId);
    setDragOffset({ ox: e.clientX - rect.left, oy: e.clientY - rect.top });
  }, [readOnly]);

  useEffect(() => {
    if (!draggingId) return;
    const handleMove = (e: MouseEvent) => {
      if (!previewRef.current || !onLinePositionChange) return;
      const containerRect = previewRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(95, ((e.clientX - containerRect.left - dragOffset.ox) / containerRect.width) * 100));
      const y = Math.max(0, Math.min(95, ((e.clientY - containerRect.top - dragOffset.oy) / containerRect.height) * 100));
      onLinePositionChange(
        draggingId,
        roundPreviewPositionPercent(x),
        roundPreviewPositionPercent(y),
      );
    };
    const handleUp = () => setDraggingId(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [draggingId, dragOffset, onLinePositionChange]);

  const duration = Math.max(totalDurationMs, 1);
  const textOnlyLineCount = lines.filter((line) => !line.type || line.type === "text").length;
  const resolvedFontFamily = sceneTextFontCss(fontFamily);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, flex: 1, minHeight: 0, height: fullScreen || readOnly ? "100%" : undefined }}>
      {!fullScreen ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 12px",
            background: isWilds ? "rgba(120,200,120,0.08)" : "rgba(126,184,255,0.08)",
            borderRadius: "10px 10px 0 0",
            border: `1px solid ${isWilds ? "rgba(120,200,120,0.25)" : "rgba(126,184,255,0.25)"}`,
            borderBottom: "none",
          }}
        >
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: accent,
          }}>
            {headerLabel ?? (readOnly
              ? t(realm, "editor_preview_label", "Preview")
              : t(realm, "editor_preview_interact_hint", "Preview — drag to position, resize from corner"))}
          </span>
          <span style={{ fontSize: 9, color: "rgba(232,240,255,0.4)" }}>
            {lines.length} {lines.length === 1 ? "line" : "lines"}
          </span>
        </div>
      ) : null}

      {/* Preview area */}
      <div
        ref={(el) => {
          previewRef.current = el;
          onPreviewAreaRef?.(el);
        }}
        style={{
          background: backgroundColor,
          borderRadius: fullScreen ? 0 : "0 0 10px 10px",
          border: fullScreen ? "none" : `1px solid ${isWilds ? "rgba(120,200,120,0.25)" : "rgba(126,184,255,0.25)"}`,
          flex: 1,
          minHeight: fullScreen || readOnly ? 0 : 120,
          padding: 0,
          position: "relative",
          overflow: "hidden",
          containerType: "size",
          boxShadow: fullScreen ? "none" : "0 4px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
          cursor: draggingId ? "grabbing" : "default",
        }}
      >
        {visibleLines.map((line, idx) =>
          line.opacity > 0 ? (
            (!line.type || line.type === "text") ? (
              <PreviewTextLine
                key={line.id}
                line={line}
                idx={idx}
                textOnlyLineCount={textOnlyLineCount}
                readOnly={readOnly}
                previewRef={previewRef}
                draggingId={draggingId}
                accent={accent}
                resolvedFontFamily={resolvedFontFamily}
                textColor={textColor}
                backgroundColor={backgroundColor}
                fontSize={fontSize}
                onDragStart={handleDragStart}
                onResize={onEffectResize}
              />
            ) : (
              <PreviewEffectLine
                key={line.id}
                line={line}
                readOnly={readOnly}
                previewRef={previewRef}
                draggingId={draggingId}
                isWilds={isWilds}
                selectedEffectId={selectedEffectId}
                mediaUrls={mediaUrls}
                onDragStart={handleDragStart}
                onResize={onEffectResize}
              />
            )
          ) : null
        )}
        {visibleLines.every((l) => l.opacity === 0) && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: "rgba(128,128,128,0.5)", fontSize: 12, fontStyle: "italic" }}>
              {t(realm, "editor_preview_empty_hint", "Preview will appear here")}
            </div>
          </div>
        )}
        {overlay}
      </div>
      <style>{dramaticTextStylesheet()}</style>

      {showTransport ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={isPlaying ? onPause : onPlay}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid rgba(232,240,255,0.2)",
              background: "rgba(232,240,255,0.06)",
              color: "#e8f0ff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
            }}
            title={isPlaying ? t(realm, "editor_preview_pause", "Pause") : t(realm, "editor_preview_play", "Play")}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
          <input
            type="range"
            min={0}
            max={duration}
            value={playheadMs}
            onChange={(e) => onSeek(Number(e.target.value))}
            style={{ flex: 1, height: 4, accentColor: accent as string }}
          />
          <div style={{ fontSize: 10, color: "rgba(232,240,255,0.5)", fontVariantNumeric: "tabular-nums", minWidth: 50, textAlign: "right" }}>
            {formatMs(playheadMs)} / {formatMs(duration)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Line Timing Controls ─── */

interface LineTimingControlsProps {
  line: TextLine;
  realm: RealmId;
  onUpdate: (update: Partial<TextLine>) => void;
}

export function LineTimingControls({ line, realm, onUpdate }: LineTimingControlsProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const persist = line.persist !== false;

  const isSound = line.type === "sound";
  const fadeInLabel = isSound
    ? t(realm, "editor_effect_fade_in_sound", "Fade In Sound")
    : t(realm, "editor_line_fade_in", "Fade In");
  const fadeOutLabel = isSound
    ? t(realm, "editor_effect_fade_out_sound", "Fade Out Sound")
    : t(realm, "editor_line_fade_out", "Fade Out");

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
      <TimingField
        label={t(realm, "editor_line_timestamp", "Appears at")}
        valueMs={line.appear_at_ms}
        onChange={(v) => onUpdate({ appear_at_ms: v })}
      />
      <TimingField
        label={fadeInLabel}
        valueMs={line.fade_in_ms}
        onChange={(v) => onUpdate({ fade_in_ms: v })}
      />
      <TimingField
        label={t(realm, "editor_line_duration", "Duration")}
        valueMs={line.duration_ms}
        onChange={(v) => onUpdate({ duration_ms: v })}
      />
      <TimingField
        label={fadeOutLabel}
        valueMs={line.fade_out_ms}
        onChange={(v) => onUpdate({ fade_out_ms: v })}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 8, color: "rgba(232,240,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {t(realm, "editor_line_persist", "Stay on Screen")}
        </span>
        <button
          type="button"
          onClick={() => onUpdate({ persist: !persist })}
          style={{
            height: 22,
            padding: "0 8px",
            borderRadius: 5,
            border: `1px solid ${persist ? accent : "rgba(232,240,255,0.15)"}`,
            background: persist ? (isWilds ? "rgba(120,200,120,0.18)" : "rgba(126,184,255,0.18)") : "rgba(232,240,255,0.04)",
            color: persist ? accent : "rgba(232,240,255,0.5)",
            fontSize: 10,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s",
          }}
        >
          {persist ? "ON" : "OFF"}
        </button>
      </div>
    </div>
  );
}

function TimingField({ label, valueMs, onChange, step = 100 }: { label: string; valueMs: number; onChange: (ms: number) => void; step?: number }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueMsRef = useRef(valueMs);
  valueMsRef.current = valueMs;

  const clearHoldTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  useEffect(() => clearHoldTimers, [clearHoldTimers]);

  const startHold = useCallback((direction: 1 | -1) => {
    clearHoldTimers();
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        onChange(Math.max(0, valueMsRef.current + step * direction));
      }, 50);
    }, 300);
  }, [onChange, step, clearHoldTimers]);

  const handleStart = () => {
    setDraft((valueMs / 1000).toFixed(1));
    setEditing(true);
  };

  const handleCommit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(Math.round(parsed * 1000));
    }
    setEditing(false);
  };

  const increment = () => onChange(Math.max(0, valueMs + step));
  const decrement = () => onChange(Math.max(0, valueMs - step));

  const arrowBtnStyle: React.CSSProperties = {
    width: 18,
    height: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "rgba(232,240,255,0.06)",
    color: "rgba(232,240,255,0.55)",
    cursor: "pointer",
    padding: 0,
    fontSize: 8,
    transition: "background 0.12s, color 0.12s",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 8, color: "rgba(232,240,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "center", borderRadius: 5, overflow: "hidden", border: "1px solid rgba(232,240,255,0.15)", background: "rgba(232,240,255,0.04)" }}>
        <button
          type="button"
          onClick={decrement}
          onMouseDown={() => startHold(-1)}
          onMouseUp={clearHoldTimers}
          onMouseLeave={clearHoldTimers}
          style={{ ...arrowBtnStyle, borderRight: "1px solid rgba(232,240,255,0.1)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(126,184,255,0.15)"; e.currentTarget.style.color = "#e8f0ff"; }}
          title="Decrease (hold to scrub)"
        >
          ◀
        </button>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleCommit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") handleCommit();
              if (e.key === "Escape") setEditing(false);
              if (e.key === "ArrowUp") { e.preventDefault(); const v = parseFloat(draft); if (!isNaN(v)) setDraft((v + step / 1000).toFixed(1)); }
              if (e.key === "ArrowDown") { e.preventDefault(); const v = parseFloat(draft); if (!isNaN(v)) setDraft(Math.max(0, v - step / 1000).toFixed(1)); }
            }}
            style={{
              width: 42,
              fontSize: 11,
              padding: "3px 2px",
              border: "none",
              background: "rgba(126,184,255,0.1)",
              color: "#e8f0ff",
              textAlign: "center",
              outline: "none",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={handleStart}
            style={{
              width: 42,
              fontSize: 11,
              padding: "3px 2px",
              border: "none",
              background: "transparent",
              color: "#e8f0ff",
              cursor: "pointer",
              textAlign: "center",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {(valueMs / 1000).toFixed(1)}s
          </button>
        )}
        <button
          type="button"
          onClick={increment}
          onMouseDown={() => startHold(1)}
          onMouseUp={clearHoldTimers}
          onMouseLeave={clearHoldTimers}
          style={{ ...arrowBtnStyle, borderLeft: "1px solid rgba(232,240,255,0.1)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(126,184,255,0.15)"; e.currentTarget.style.color = "#e8f0ff"; }}
          title="Increase (hold to scrub)"
        >
          ▶
        </button>
      </div>
    </div>
  );
}

/* ─── Main export: createDefaultLine helper ─── */

export function createDefaultLine(appearAtMs: number = 0): TextLine {
  return {
    id: genId(),
    html: "",
    appear_at_ms: appearAtMs,
    fade_in_ms: 500,
    fade_out_ms: 500,
    duration_ms: 3000,
    persist: true,
    ...SCROLLABLE_TEXT_FILL,
  };
}

/** Playhead position where timed content is fully visible (after fade-in). */
export function previewMsWhenVisible(timing: {
  appear_at_ms: number;
  fade_in_ms: number;
}): number {
  return timing.appear_at_ms + timing.fade_in_ms;
}

export function computeTotalDuration(lines: TextLine[]): number {
  if (lines.length === 0) return 5000;
  return Math.max(
    ...lines.map((l) => l.appear_at_ms + l.fade_in_ms + l.duration_ms + l.fade_out_ms),
    5000,
  );
}

/* ─── Choice timing helpers ─── */

export interface ChoiceTiming {
  appear_at_ms: number;
  fade_in_ms: number;
  duration_ms: number;
  fade_out_ms: number;
}

export function resolveChoiceTiming(
  choice: SceneChoice,
  fallbackAppearMs: number,
): ChoiceTiming {
  const appearMs = choice.start_time_seconds != null
    ? Math.round(choice.start_time_seconds * 1000)
    : fallbackAppearMs;
  const fadeIn = choice.fade_in_ms ?? 400;
  const fadeOut = choice.fade_out_ms ?? 400;
  const endMs = choice.end_time_seconds != null
    ? Math.round(choice.end_time_seconds * 1000)
    : appearMs + fadeIn + (choice.duration_ms ?? 2000) + fadeOut;
  const duration = choice.duration_ms ?? Math.max(500, endMs - appearMs - fadeIn - fadeOut);
  return {
    appear_at_ms: appearMs,
    fade_in_ms: fadeIn,
    duration_ms: duration,
    fade_out_ms: fadeOut,
  };
}

export function computeChoiceOpacity(timing: ChoiceTiming, playheadMs: number): number {
  const start = timing.appear_at_ms;
  const fadeInEnd = start + timing.fade_in_ms;
  const holdEnd = fadeInEnd + timing.duration_ms;
  const fadeOutEnd = holdEnd + timing.fade_out_ms;

  if (playheadMs < start) return 0;
  if (playheadMs < fadeInEnd) {
    return timing.fade_in_ms > 0 ? (playheadMs - start) / timing.fade_in_ms : 1;
  }
  if (playheadMs < holdEnd) return 1;
  if (playheadMs < fadeOutEnd) {
    return timing.fade_out_ms > 0 ? 1 - (playheadMs - holdEnd) / timing.fade_out_ms : 0;
  }
  return 0;
}

export function choiceTimingToLine(
  choiceId: string,
  timing: ChoiceTiming,
): TextLine {
  return {
    id: choiceId,
    html: "",
    appear_at_ms: timing.appear_at_ms,
    fade_in_ms: timing.fade_in_ms,
    duration_ms: timing.duration_ms,
    fade_out_ms: timing.fade_out_ms,
    persist: false,
  };
}

export function timingToChoiceSeconds(timing: ChoiceTiming): {
  start_time_seconds: number;
  end_time_seconds: number;
} {
  const endMs = timing.appear_at_ms + timing.fade_in_ms + timing.duration_ms + timing.fade_out_ms;
  return {
    start_time_seconds: timing.appear_at_ms / 1000,
    end_time_seconds: endMs / 1000,
  };
}

export function computeEffectiveSceneDuration(
  textDurationMs: number,
  choices: SceneChoice[],
  sceneDurationSec: number | null,
): number {
  if (sceneDurationSec != null) {
    return Math.max(sceneDurationSec * 1000, 1000);
  }
  const choiceEnds = choices.map((c) => {
    const t = resolveChoiceTiming(c, textDurationMs);
    return t.appear_at_ms + t.fade_in_ms + t.duration_ms + t.fade_out_ms;
  });
  return Math.max(textDurationMs, ...choiceEnds, 5000);
}

export type ChoiceFlowItem =
  | { kind: "text"; id: string; sortMs: number; line: TextLine; index: number }
  | { kind: "effect"; id: string; sortMs: number; line: TextLine; index: number }
  | { kind: "choice"; id: string; sortMs: number; choice: SceneChoice; index: number }
  | { kind: "goto"; id: string; sortMs: number }
  | { kind: "add_choice"; id: string; sortMs: number };

const FLOW_KIND_ORDER = {
  text: 0,
  effect: 1,
  choice: 2,
  add_choice: 3,
  goto: 4,
} as const;

export function buildChoiceFlowItems(
  textLines: TextLine[],
  choices: SceneChoice[],
  effectiveDurationMs: number,
  fallbackAppearMs: number,
  canAddChoice: boolean,
): ChoiceFlowItem[] {
  const textOnlyLines = textLines.filter((l) => !l.type || l.type === "text");
  const effectLines = textLines.filter((l) => l.type === "sound" || l.type === "image");
  const items: ChoiceFlowItem[] = [];

  textOnlyLines.forEach((line, index) => {
    items.push({
      kind: "text",
      id: line.id,
      sortMs: line.appear_at_ms,
      line,
      index,
    });
  });

  effectLines.forEach((line, index) => {
    items.push({
      kind: "effect",
      id: line.id,
      sortMs: line.appear_at_ms,
      line,
      index,
    });
  });

  choices.forEach((choice, index) => {
    const timing = resolveChoiceTiming(choice, fallbackAppearMs);
    items.push({
      kind: "choice",
      id: choice.id,
      sortMs: timing.appear_at_ms,
      choice,
      index,
    });
  });

  if (canAddChoice) {
    items.push({
      kind: "add_choice",
      id: "__add_choice__",
      sortMs: fallbackAppearMs,
    });
  }

  items.push({
    kind: "goto",
    id: "__goto__",
    sortMs: effectiveDurationMs,
  });

  return items.sort((a, b) => {
    if (a.sortMs !== b.sortMs) return a.sortMs - b.sortMs;
    return FLOW_KIND_ORDER[a.kind] - FLOW_KIND_ORDER[b.kind];
  });
}
